import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Mail, CheckCircle, AlertCircle, ExternalLink, Send, Copy, Check } from "lucide-react";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA = { fontFamily: "'Lora', Georgia, serif" };

const DNS_STEPS = [
  {
    step: 1,
    title: "Ajouter le domaine dans Resend",
    desc: "Va sur le tableau de bord Resend → Domains → Add Domain, puis entre : voyage-r.fr",
    action: { label: "Ouvrir Resend Domains", href: "https://resend.com/domains" },
  },
  {
    step: 2,
    title: "Récupérer les enregistrements DNS",
    desc: "Resend te donnera 3 enregistrements à ajouter chez ton hébergeur DNS (OVH, Gandi, etc.) : SPF, DKIM et DMARC.",
  },
  {
    step: 3,
    title: "Ajouter les enregistrements chez ton registrar",
    desc: "Copie exactement les valeurs fournies par Resend dans la zone DNS de voyage-r.fr. La propagation DNS prend 5 à 30 minutes.",
  },
  {
    step: 4,
    title: "Vérifier le domaine dans Resend",
    desc: "Clique sur Verify Domain dans Resend. Une fois vérifié, les emails partiront directement depuis diego@voyage-r.fr.",
    action: { label: "Vérifier dans Resend", href: "https://resend.com/domains" },
  },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-1 text-xs text-[#C17F59] border border-[#DCD4C6] hover:bg-[#F1EBE1] transition-colors"
      style={{ ...LORA, borderRadius: "2px" }}
    >
      {copied ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.5} />}
      {copied ? "Copié" : "Copier"}
    </button>
  );
}

export default function AdminEmailTab() {
  const [sending, setSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/email/status`)
      .then(({ data }) => setEmailStatus(data))
      .catch(() => {});
  }, []);

  const domainVerified = emailStatus?.domain_verified ?? false;
  const senderEmail = emailStatus?.sender_email ?? "diego@voyage-r.fr";

  const sendTestEmail = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/email/test`, {});
      toast.success(data.message);
    } catch {
      toast.error("Erreur lors de l'envoi de l'email de test");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="email-tab">
      {/* Header */}
      <div>
        <p className="section-label mb-1">Configuration</p>
        <h2 className="text-3xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Email & Domaine</h2>
        <p className="text-sm text-[#5C554E] italic mt-1" style={LORA}>
          Gère l'expéditeur des emails envoyés à tes clients
        </p>
      </div>

      {/* Statut actuel */}
      <div className="card-surface">
        <h3 className="text-lg mb-4" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Statut de l'expéditeur</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-[#F9F6F0] border border-[#DCD4C6] p-4">
            <p className="text-xs uppercase tracking-widest text-[#9A8F86] mb-2" style={LORA}>Expéditeur actuel</p>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm text-[#2B2623]">{senderEmail}</span>
              <CopyButton text={senderEmail} />
            </div>
          </div>
          <div className="bg-[#F9F6F0] border border-[#DCD4C6] p-4">
            <p className="text-xs uppercase tracking-widest text-[#9A8F86] mb-2" style={LORA}>Statut du domaine</p>
            <div className="flex items-center gap-2">
              {domainVerified ? (
                <>
                  <CheckCircle size={16} strokeWidth={1.5} className="text-emerald-600" />
                  <span className="text-sm text-emerald-700" style={LORA}>voyage-r.fr vérifié</span>
                </>
              ) : (
                <>
                  <AlertCircle size={16} strokeWidth={1.5} className="text-amber-600" />
                  <span className="text-sm text-amber-700" style={LORA}>En attente de vérification</span>
                </>
              )}
            </div>
          </div>
        </div>

        {!domainVerified && (
          <div className="mt-4 bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3">
            <AlertCircle size={15} strokeWidth={1.5} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800" style={LORA}>
              Tant que <strong>voyage-r.fr</strong> n'est pas vérifié dans Resend, les emails sont redirigés vers
              <strong> diego@voyage-r.fr</strong> à titre de fallback. Suis les étapes ci-dessous pour activer l'envoi direct.
            </p>
          </div>
        )}
      </div>

      {/* Guide de vérification */}
      <div className="card-surface">
        <h3 className="text-lg mb-6" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
          Vérifier le domaine voyage-r.fr
        </h3>
        <div className="space-y-6">
          {DNS_STEPS.map(({ step, title, desc, action }) => (
            <div key={step} className="flex gap-4">
              <div className="flex-shrink-0 w-7 h-7 border-2 border-[#C17F59] rounded-full flex items-center justify-center">
                <span className="text-xs font-semibold text-[#C17F59]" style={LORA}>{step}</span>
              </div>
              <div className="flex-1 pt-0.5">
                <p className="text-sm font-semibold text-[#2B2623] mb-1" style={LORA}>{title}</p>
                <p className="text-sm text-[#5C554E] leading-relaxed" style={LORA}>{desc}</p>
                {action && (
                  <a
                    href={action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-sm text-[#C17F59] hover:text-[#8B5E34] transition-colors"
                    style={LORA}
                  >
                    <ExternalLink size={12} strokeWidth={1.5} />
                    {action.label}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Types d'emails envoyés */}
      <div className="card-surface">
        <h3 className="text-lg mb-4" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Emails transactionnels</h3>
        <div className="space-y-2">
          {[
            { label: "Bienvenue", desc: "Envoyé à l'inscription de chaque utilisateur", icon: "👋" },
            { label: "Voyage prêt", desc: "Envoyé après ta validation d'un itinéraire", icon: "✅" },
            { label: "Paiement confirmé", desc: "Envoyé automatiquement après chaque paiement Stripe", icon: "💳" },
          ].map(({ label, desc, icon }) => (
            <div key={label} className="flex items-center gap-3 py-2.5 border-b border-[#EDE8E0] last:border-0">
              <span className="text-base">{icon}</span>
              <div>
                <p className="text-sm font-semibold text-[#2B2623]" style={LORA}>{label}</p>
                <p className="text-xs text-[#9A8F86] italic" style={LORA}>{desc}</p>
              </div>
              <div className="ml-auto">
                <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200" style={{ ...LORA, borderRadius: "2px" }}>
                  Actif
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test email */}
      <div className="card-surface flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-[#2B2623]" style={LORA}>Tester la configuration</p>
          <p className="text-xs text-[#9A8F86] italic mt-0.5" style={LORA}>
            Envoie un email de test à diego@voyage-r.fr pour vérifier que tout fonctionne
          </p>
        </div>
        <button
          onClick={sendTestEmail}
          disabled={sending}
          className="btn-primary flex items-center gap-2 text-sm flex-shrink-0"
          data-testid="send-test-email-btn"
        >
          <Send size={13} strokeWidth={1.5} />
          {sending ? "Envoi..." : "Envoyer un test"}
        </button>
      </div>
    </div>
  );
}
